import { useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/LanguageContext";

const NODES = [
  { id: "core", label: "Query Intent", x: 300, y: 200, r: 22, hub: true },
  { id: "sem", label: "Semantics", x: 150, y: 110, r: 15 },
  { id: "lex", label: "Lexical", x: 130, y: 300, r: 14 },
  { id: "kg", label: "Knowledge Graph", x: 470, y: 120, r: 16 },
  { id: "rank", label: "Ranking", x: 490, y: 300, r: 15 },
  { id: "embed", label: "Embeddings", x: 300, y: 60, r: 12 },
  { id: "rel", label: "Relations", x: 560, y: 210, r: 12 },
  { id: "src", label: "Sources", x: 300, y: 350, r: 13 },
  { id: "clu", label: "Clusters", x: 60, y: 200, r: 11 },
];

const EDGES = [
  ["core", "sem"],
  ["core", "lex"],
  ["core", "kg"],
  ["core", "rank"],
  ["core", "embed"],
  ["core", "src"],
  ["sem", "embed"],
  ["sem", "clu"],
  ["lex", "clu"],
  ["kg", "rel"],
  ["kg", "rank"],
  ["rank", "src"],
  ["rel", "rank"],
];

const nodeById = Object.fromEntries(NODES.map((n) => [n.id, n]));

export const KnowledgeGraphWidget = () => {
  const { t } = useI18n();
  const [active, setActive] = useState("core");

  const isConnected = (id) =>
    id === active ||
    EDGES.some(
      ([a, b]) => (a === active && b === id) || (b === active && a === id)
    );

  return (
    <div
      data-testid="knowledge-graph-widget"
      className="relative w-full aspect-[3/2] rounded-2xl glass overflow-hidden grid-lines"
    >
      <div className="absolute inset-0 bg-gradient-to-tr from-[#05101f] via-transparent to-[#1E6FFF]/10" />
      <svg viewBox="0 0 620 400" className="relative w-full h-full">
        <defs>
          <filter id="glowFilter" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {EDGES.map(([a, b], i) => {
          const na = nodeById[a];
          const nb = nodeById[b];
          const hot = a === active || b === active;
          return (
            <motion.line
              key={i}
              x1={na.x}
              y1={na.y}
              x2={nb.x}
              y2={nb.y}
              stroke={hot ? "#4BE3FF" : "#1E6FFF"}
              strokeWidth={hot ? 1.8 : 1}
              strokeOpacity={hot ? 0.9 : 0.28}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.1, delay: i * 0.05 }}
            />
          );
        })}

        {NODES.map((n) => {
          const connected = isConnected(n.id);
          const isActive = n.id === active;
          return (
            <g
              key={n.id}
              onMouseEnter={() => setActive(n.id)}
              className="cursor-pointer"
              data-testid={`graph-node-${n.id}`}
            >
              {isActive && (
                <motion.circle
                  cx={n.x}
                  cy={n.y}
                  r={n.r}
                  fill="#4BE3FF"
                  opacity={0.4}
                  animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                />
              )}
              <motion.circle
                cx={n.x}
                cy={n.y}
                r={n.r}
                fill={isActive ? "#4BE3FF" : connected ? "#1E6FFF" : "#0A1A2F"}
                stroke={connected ? "#4BE3FF" : "#A7B4C2"}
                strokeWidth={isActive ? 2 : 1}
                filter={connected ? "url(#glowFilter)" : "none"}
                opacity={connected ? 1 : 0.4}
                whileHover={{ scale: 1.15 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
              <text
                x={n.x}
                y={n.y + n.r + 14}
                textAnchor="middle"
                className="font-mono pointer-events-none select-none"
                fontSize="11"
                fill={connected ? "#F5F7FA" : "#A7B4C2"}
                opacity={connected ? 1 : 0.5}
              >
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="absolute bottom-4 left-4 font-mono text-[11px] text-[#A7B4C2]">
        <span className="text-[#4BE3FF]">●</span> {t("graph.activeNode")} —{" "}
        <span className="text-white">{nodeById[active].label}</span>
      </div>
    </div>
  );
};
